import React, { useState, forwardRef, useEffect } from 'react';
import CheckCircleOutlineSharpIcon from '@mui/icons-material/CheckCircleOutlineSharp';
import HighlightOffSharpIcon from '@mui/icons-material/HighlightOffSharp';
import ErrorOutlineSharpIcon from '@mui/icons-material/ErrorOutlineSharp';
import { East, Close, ArrowDropDown } from '@mui/icons-material';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InfoIcon from '@mui/icons-material/Info';
import { AlertCircle, CheckCircle, CircleAlert } from 'lucide-react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  Button,
  Tooltip,
  IconButton,
  Slide,
  MenuItem,
  CircularProgress,
  Popover,
  TextField,
  Checkbox
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { stateNameMap } from '../../utils/stateNames';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction='left' ref={ref} {...props} />;
});

function AnalysisCard({ cardData, cardType, selectedLob }) {
  const [openPopup, setOpenPopup] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionTaken, setActionTaken] = useState(null);
  const [appetiteInfo, setAppetiteInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [borderColor, setBorderColor] = useState('#ccc');
  const [sortDirection, setSortDirection] = useState('desc'); // 'desc' means high relevancy first
  const [sortColumn, setSortColumn] = useState('relevancy'); // or 'cmi'
  const [cmiFilter, setCmiFilter] = useState([]);
  const [cmiFilterAnchor, setCmiFilterAnchor] = useState(null);
  const [cmiSearch, setCmiSearch] = useState('');
  const [infoAnchor, setInfoAnchor] = useState(null); // New state for info popover
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleIconClick = () => {
    setOpenPopup(true);
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setActionTaken('');
  };

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = action => {
    setActionTaken(action);
    handleMenuClose();
  };

  const toggleSort = () => {
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const mapRelevancy = r => {
    if (!r) return 0;
    const lower = r.toLowerCase();
    if (lower === 'high') return 3;
    if (lower === 'medium') return 2;
    if (lower === 'low') return 1;
    return 0;
  };

  const getRelevancyIcon = relevancy => {
    const r = relevancy?.toLowerCase();
    if (r === 'high')
      return (
        <KeyboardDoubleArrowUpIcon
          style={{ color: 'green' }}
          fontSize='small'
        />
      );
    else if (r === 'medium')
      return (
        <KeyboardDoubleArrowRightIcon
          style={{ color: 'orange' }}
          fontSize='small'
        />
      );
    else if (r === 'low')
      return (
        <KeyboardDoubleArrowDownIcon
          style={{ color: 'red' }}
          fontSize='small'
        />
      );
    return null;
  };

  useEffect(() => {
    setIsLoading(true);
    if (cardData) {
      setTimeout(() => {
        setAppetiteInfo(cardData);
      }, 1000);
    } else {
      setAppetiteInfo({});
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    if (cardType === 'Do Not Write' && cardData?.Summary?.score) {
      setBorderColor(cardData?.Summary?.score === 10 ? 'green' : 'red');
    } else {
      if (cardData?.Summary?.score < 5) {
        setBorderColor('red');
      } else if (cardData?.Summary?.score < 7) {
        setBorderColor('orange');
      } else if (cardData?.Summary?.score >= 7) {
        setBorderColor('green');
      }
    }
  }, [cardData, cardType, selectedLob]);

  function sentenceCase(str) {
    if (!str) return '';
    const lower = str.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  // Compute unique CMIs from appetiteInfo.AppetiteDetails:
  const uniqueCMIs = Array.from(
    new Set(
      Object.keys(appetiteInfo?.AppetiteDetails || {}).map(
        key => appetiteInfo.AppetiteDetails[key].cmi
      )
    )
  );

  const filteredCMIs = uniqueCMIs.filter(cmi =>
    cmi?.toString().toLowerCase().includes(cmiSearch.toLowerCase())
  );

  // Build details array and apply sorting based on the current sortColumn &
  // sortDirection, then filter if needed.
  const detailsArray = Object.keys(appetiteInfo?.AppetiteDetails || {}).map(
    key => ({ key, ...appetiteInfo.AppetiteDetails[key] })
  );

  let sortedDetails = [];
  if (sortColumn === 'cmi') {
    sortedDetails = detailsArray.sort((a, b) =>
      sortDirection === 'asc' ? a.cmi - b.cmi : b.cmi - a.cmi
    );
  } else {
    sortedDetails = detailsArray.sort((a, b) => {
      const diff = mapRelevancy(a.relevancy) - mapRelevancy(b.relevancy);
      return sortDirection === 'asc' ? diff : -diff;
    });
  }

  // Apply filter on the CMI column if any filters are selected
  if (cmiFilter.length > 0) {
    sortedDetails = sortedDetails.filter(detail =>
      cmiFilter.includes(detail.cmi)
    );
  }

  const filteredDetails = sortedDetails.filter(detail =>
    cmiFilter.length > 0 ? cmiFilter.includes(detail.cmi) : true
  );

  // New functions to handle Info icon popover.
  const handleInfoClick = event => {
    setInfoAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchor(null);
  };

  return (
    <div className='w-2/4 stage-types text-sm font-normal'>
      <div
        className={`rounded-md p-3 aspect-square ${cardData?.Summary?.color === 'yellow'
          ? 'bg-yellow-50'
          : cardData?.Summary?.color === 'red'
            ? 'bg-red-50'
            : cardData?.Summary?.color === 'green'
              ? 'bg-green-50'
              : cardType.includes('Desk')
                ? 'bg-blue-50'
                : cardType.includes('Comments')
                  ? 'bg-yellow-50'
                  : 'bg-pink-50'
          }  font-medium min-h-[300px]`}
      >
        <div className='appetite-conten'>
          <div className='flex items-center gap-1 mb-2'>
            {cardType === 'Do Not Write' && (
              <>
                <CircleAlert className='h-4 w-4 text-red-500' />
                <h4 className='text-sm font-medium text-red-700'>{cardType}</h4>
              </>
            )}
            {cardType.includes('Desk') && (
              <>
                <CheckCircle className='h-4 w-4 text-blue-500' />
                <h4 className='text-sm font-medium text-blue-700'>
                  {cardType}
                </h4>
              </>
            )}
            {cardType.includes('Comments') && (
              <>
                <AlertCircle className='h-4 w-4 text-yellow-500' />
                <h4 className='text-sm font-medium text-yellow-700'>
                  {cardType}
                </h4>
              </>
            )}
            {cardType.includes('Property') && (
              <>
                <CheckCircle className='h-4 w-4 text-blue-500' />
                <h4 className='text-sm font-medium text-blue-700'>
                  {cardType}
                </h4>
              </>
            )}
          </div>
          {isLoading ? (
            <div className='flex justify-center items-center w-full h-full p-4'>
              <CircularProgress color='warning' />
            </div>
          ) : appetiteInfo?.Summary?.AppetiteSummary ? (
            <div
              className='min-h-[250px] h-[250px] overflow-y-auto'
              dangerouslySetInnerHTML={{
                __html: appetiteInfo.Summary.AppetiteSummary
              }}
            />
          ) : appetiteInfo?.summary ? (
            <div
              className='min-h-[250px] h-[250px] overflow-y-auto'
              dangerouslySetInnerHTML={{
                __html: appetiteInfo.summary
              }}
            />
          ) : (
            'No summary available'
          )}
        </div>
        {cardType !== 'Do Not Write' &&
          (appetiteInfo?.Summary?.AppetiteSummary || appetiteInfo?.summary) && (
            <Button
              variant='contained'
              size='small'
              color='primary'
              className='w-full mt-2 text-sm h-6 rounded-md font-medium shadow-md hover:shadow-md transition-all duration-200'
              onClick={handleIconClick}
              endIcon={<East fontSize='small' />}
              sx={{
                background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #1565c0, #1976d2)'
                },
                textTransform: 'none'
              }}
            >
              View Details
            </Button>
          )}
      </div>
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        TransitionComponent={Transition}
        fullWidth
        maxWidth='900px'
        PaperProps={{
          style: {
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            margin: 0,
            width: '900px',
            borderRadius: '12px',
            background: 'white',
            border: 'none'
          }
        }}
      >
        <DialogTitle className='flex justify-between items-center p-4 border-b'>
          <div className='text-xl font-semibold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent'>
            {cardType}
          </div>
          {appetiteInfo?.['Summary']?.Escalatable && !actionTaken && (
            <div
              className='flex items-center cursor-pointer  font-normal ml-auto'
              onClick={handleMenuClick}
            >
              <span>Action</span>
              <ArrowDropDown />
            </div>
          )}
          {appetiteInfo?.['Summary']?.Escalatable && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                style: {
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <Tooltip title='Actions' arrow>
                <IconButton onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={() => handleActionClick('Escalate')}>
                  Escalate
                </MenuItem>
              </Menu>
            </Menu>
          )}
          <IconButton onClick={handleClosePopup}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className='p-6'>
          <div className='grid grid-cols-1 gap-6'>
            {!actionTaken &&
              cardType.includes('Comments') &&
              Object.keys(appetiteInfo).length > 0 && (
                <>
                  <div className='space-y-4'>
                    <table className='w-full border-collapse table-fixed'>
                      <thead className='bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent'>
                        <tr>
                          {/* CMI Column Header with sort and filter */}
                          <th
                            className='border border-gray-300 w-[10%] flex items-center justify-between cursor-pointer'
                            onClick={() => {
                              if (sortColumn === 'cmi') {
                                setSortDirection(prev =>
                                  prev === 'asc' ? 'desc' : 'asc'
                                );
                              } else {
                                setSortColumn('cmi');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            <span>CMI</span>
                            {sortColumn === 'cmi' &&
                              (sortDirection === 'asc' ? (
                                <ArrowUpwardIcon fontSize='small' />
                              ) : (
                                <ArrowDownwardIcon fontSize='small' />
                              ))}
                            <IconButton
                              onClick={e => {
                                e.stopPropagation();
                                setCmiFilterAnchor(e.currentTarget);
                              }}
                              size='small'
                            >
                              <FilterListIcon fontSize='small' />
                            </IconButton>
                          </th>
                          {/* Comment Column */}
                          <th className='border border-gray-300 p-2 w-[75%]'>
                            Comment
                          </th>
                          {/* Relevancy Column Header */}
                          <th
                            className='border border-gray-300 p-2 w-[15%] text-center cursor-pointer'
                            onClick={() => {
                              if (sortColumn === 'relevancy') {
                                setSortDirection(prev =>
                                  prev === 'asc' ? 'desc' : 'asc'
                                );
                              } else {
                                setSortColumn('relevancy');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            Relevancy{' '}
                            {sortColumn === 'relevancy' &&
                              (sortDirection === 'asc' ? (
                                <ArrowUpwardIcon fontSize='small' />
                              ) : (
                                <ArrowDownwardIcon fontSize='small' />
                              ))}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDetails.map((detail, index) => (
                          <tr key={detail.key}>
                            <td
                              className={`border border-gray-300 p-2 font-bold ${detail.source === 'llm'
                                ? 'text-green-500'
                                : 'text-blue-500'
                                }`}
                            >
                              {detail.cmi}
                            </td>
                            <td
                              className='border border-gray-300 p-2'
                              dangerouslySetInnerHTML={{
                                __html: sentenceCase(detail.original_comment)
                              }}
                            />
                            <td className='border border-gray-300 p-2 text-center'>
                              <Tooltip
                                title={
                                  detail.relevancy
                                    ? detail.relevancy.charAt(0).toUpperCase() +
                                    detail.relevancy.slice(1)
                                    : ''
                                }
                              >
                                <span>
                                  {getRelevancyIcon(detail.relevancy)}
                                </span>
                              </Tooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

            {cardType.includes('Desk') && (
              <div className='document-details p-1.5'>
                {detailsArray && Object.keys(detailsArray).length > 0 ? (
                  Object.keys(detailsArray).map((fileKey, i, arr) => {
                    const file = detailsArray[fileKey];
                    return (
                      <div
                        key={fileKey}
                        style={{
                          marginBottom: '20px',
                          paddingBottom: '10px'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <h4 className='text-orange-600 italic m-0'>
                            {file.filename}
                          </h4>
                          <a
                            href={file.file_uri}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <PictureAsPdfIcon className='text-orange-600' />
                          </a>
                        </div>
                        <p className='m-1 italic'>
                          <strong>Type:</strong> {file.document_type}
                        </p>
                        <div
                          style={{ marginTop: '10px' }}
                          dangerouslySetInnerHTML={{
                            __html: file.consolidated_content
                          }}
                        />
                        {i !== arr.length - 1 && (
                          <hr className='mt-5 border-t border-gray-300' />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p>No document details available</p>
                )}
              </div>
            )}
            {cardType.includes('Property') && (
              <div className='property-details p-4 space-y-6'>
                {/* Insights Section */}
                <div className='bg-orange-50 rounded-lg p-4'>
                  <h3 className='text-lg font-semibold text-orange-700 mb-3 flex items-center gap-2'>
                    <InfoIcon fontSize='small' />
                    Property Insights
                  </h3>
                  <div
                    className='prose prose-sm max-w-none text-gray-700'
                    dangerouslySetInnerHTML={{
                      __html: appetiteInfo?.insights
                    }}
                  />
                </div>

                {/* Guidance Section */}
                <div className='bg-blue-50 rounded-lg p-4'>
                  <h3 className='text-lg font-semibold text-blue-700 mb-3 flex items-center gap-2'>
                    <CircleAlert className='h-5 w-5' />
                    Property Guidance
                  </h3>
                  <div
                    className='prose prose-sm max-w-none text-gray-700'
                    dangerouslySetInnerHTML={{
                      __html: appetiteInfo?.guidance
                    }}
                  />
                </div>

                {/* Related Documents */}
                {appetiteInfo?.links?.length > 0 && (
                  <div className='bg-gray-50 rounded-lg p-4'>
                    <h3 className='text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2'>
                      <PictureAsPdfIcon fontSize='small' />
                      Related Documents
                    </h3>
                    <div className='space-y-2'>
                      {appetiteInfo.links.map((link, index) => (
                        <div key={index} className='flex items-center gap-2'>
                          <PictureAsPdfIcon
                            fontSize='small'
                            className='text-orange-600'
                          />
                          <a
                            href={link}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 hover:text-blue-800 underline'
                          >
                            Document {index + 1}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CMI Filter Popover */}
          <Popover
            open={Boolean(cmiFilterAnchor)}
            anchorEl={cmiFilterAnchor}
            onClose={() => setCmiFilterAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <div style={{ padding: '16px', maxWidth: '200px' }}>
              <TextField
                label='Search CMI'
                variant='outlined'
                size='small'
                value={cmiSearch}
                onChange={e => setCmiSearch(e.target.value)}
                fullWidth
                style={{ marginBottom: '8px' }}
              />
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {filteredCMIs.map(cmi => (
                  <div
                    key={cmi}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Checkbox
                      checked={cmiFilter.includes(cmi)}
                      onChange={e => {
                        if (e.target.checked) {
                          setCmiFilter(prev => [...prev, cmi]);
                        } else {
                          setCmiFilter(prev =>
                            prev.filter(item => item !== cmi)
                          );
                        }
                      }}
                    />
                    <span>{cmi}</span>
                  </div>
                ))}
              </div>
            </div>
          </Popover>
        </DialogContent>
        {/* Sticky Footer */}
        {cardType.includes('Comments') && (
          <div className='sticky bottom-0 bg-gray-50 border-t border-gray-200 p-3 font-medium flex justify-center gap-8'>
            <div>Line Of Business: {sortedDetails[0]?.lob || 'N/A'} </div>
            <div>Rate Date: {sortedDetails[0]?.ratedate || 'N/A'} </div>
            <div>
              State:{' '}
              {stateNameMap[sortedDetails[0]?.state_name] ||
                sortedDetails[0]?.state_name ||
                'N/A'}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default AnalysisCard;
